import api from "./apiBase";

/**
 * Monta um FormData no formato esperado pelo backend.
 * @param {{ description: string, contactEmail?: string, files?: File[] }} payload
 */
function createQuotationFormData({
  description,
  contactEmail,
  contactWhats,
  files = [],
}) {
  const fd = new FormData();
  fd.set("description", description);
  if (contactEmail) fd.set("contactEmail", contactEmail);
  if (contactWhats) fd.set("contactWhats", contactWhats);
  for (const f of files) {
    if (f) fd.append("files", f, f.name);
  }
  return fd;
}

/**
 * Envia a solicitação de cotação (sempre multipart/form-data).
 * Espera receber um FormData (use createQuotationFormData).
 */
function submitQuotationRequest(formData) {
  return api.post("/quotations", formData);
}

/**
 * Cria uma cotação por upload de arquivo (multipart/form-data).
 *
 * Backend espera:
 * - file (ou modelFile) => arquivo STL/3MF
 * - material + quality => obrigatórios apenas para STL
 *
 * @param {Object} payload
 * @param {File|Blob} payload.file Arquivo principal (STL ou 3MF)
 * @param {string|null} [payload.material] Ex.: "PLA", "ABS", "PETG" (somente STL)
 * @param {string|null} [payload.quality] Ex.: "fast", "standard", "high" (somente STL)
 * @param {string} [payload.fileFieldname="file"] Nome do field multipart do arquivo (default "file")
 *
 * @returns {Promise<any>} response.data do backend
 */
async function createFileQuotation({
  file,
  material = null,
  quality = null,
  fileFieldname = "file",
} = {}) {
  if (!file) throw new Error("Arquivo é obrigatório para criar cotação.");

  const formData = new FormData();

  formData.append(fileFieldname, file);
  if (material != null) formData.append("material", String(material));
  if (quality != null) formData.append("quality", String(quality));

  try {
    const response = await api.post("/file-quotations", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  } catch (error) {
    console.error("Erro ao criar cotação por arquivo:", error);
    throw error;
  }
}

function listMyFileQuotations({ page = 1, limit = 12, search = "" } = {}) {
  const qs = new URLSearchParams();
  qs.set("page", String(page));
  qs.set("limit", String(limit));
  if (search && String(search).trim()) qs.set("search", String(search).trim());

  return api.get(`/file-quotations?${qs.toString()}`);
}

async function getFileQuotationDownloadLink({ id } = {}) {
  if (!id) throw new Error("ID da cotação é obrigatório.");

  const qs = new URLSearchParams();
  qs.set("json", "true");

  const res = await api.get(
    `/file-quotations/download/${encodeURIComponent(id)}?${qs.toString()}`,
  );

  return res.data; // { name, url }
}

/**
 * Converte uma FileQuotation finalizada em um Design.
 *
 * Backend:
 * POST /file-quotations/:fileQuotationId/convert-to-design
 *
 * @param {Object} params
 * @param {number|string} params.fileQuotationId
 *
 * @returns {Promise<{ designId: number, designInstanceId: number, plateIds: number[] }>}
 */
async function convertFileQuotationToDesign({ fileQuotationId } = {}) {
  if (!fileQuotationId) {
    throw new Error("fileQuotationId é obrigatório.");
  }

  try {
    const response = await api.post(
      `/file-quotations/${encodeURIComponent(
        fileQuotationId,
      )}/convert-to-design`,
    );

    return response.data;
  } catch (error) {
    console.error(
      "Erro ao converter cotação em modelo:",
      error?.response?.data || error,
    );
    throw error;
  }
}

/**
 * Reivindica (claim) uma cotação criada como guest e dispara o fatiamento (slicing).
 *
 * Backend:
 * POST /file-quotations/claim
 *
 * @param {Object} params
 * @param {string} params.claimKey
 *
 * @returns {Promise<any>} response.data do backend
 */
async function claimFileQuotationAndSlice({ claimKey } = {}) {
  const key = String(claimKey || "").trim();
  if (!key) throw new Error("claimKey é obrigatório.");

  try {
    const response = await api.post("/file-quotations/claim", {
      claimKey: key,
    });
    return response.data;
  } catch (error) {
    console.error(
      "Erro ao reivindicar cotação por claimKey:",
      error?.response?.data || error,
    );
    throw error;
  }
}
export {
  createQuotationFormData,
  submitQuotationRequest,
  createFileQuotation,
  listMyFileQuotations,
  getFileQuotationDownloadLink,
  convertFileQuotationToDesign,
  claimFileQuotationAndSlice,
};
